import { getDataSource } from '@/lib/data-source';

export interface RateLimits {
  per_minute?: number;
  per_hour?: number;
  per_day?: number;
}

export interface Endpoint {
  id: string;
  product_id?: string;
  productId?: string;
  name?: string;
  path?: string;
  gateway_path?: string;
  gatewayPath?: string;
  original_url?: string | null;
  originalUrl?: string | null;
  method?: string;
  methods?: string[];
  rate_limit?: number;
  rate_limits?: RateLimits | null;
  rateLimits?: RateLimits | null;
  description?: string | null;
  is_active?: boolean;
  isActive?: boolean;
  sync_status?: 'pending' | 'synced' | 'error';
  apisix_sync_status?: 'pending' | 'synced' | 'error';
  apisixSyncStatus?: 'pending' | 'synced' | 'error';
  sync_error?: string | null;
  syncError?: string | null;
  last_synced_at?: string | null;
  lastSyncedAt?: string | null;
  skip_apisix_sync?: boolean;
  skipApisixSync?: boolean;
  requires_auth?: boolean;
  requiresAuth?: boolean;
  parameters?: Record<string, unknown> | Array<Record<string, unknown>>;
  headers?: Record<string, unknown> | Array<Record<string, unknown>>;
  response_examples?: unknown;
  responseExamples?: unknown;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface EndpointCreateData {
  gateway_path: string;
  original_url: string;
  methods: string[];
  rate_limits?: RateLimits;
  description?: string;
  is_active?: boolean;
  skip_apisix_sync?: boolean;
}

export interface EndpointUpdateData extends Partial<EndpointCreateData> {}

export interface EndpointListResponse {
  data: Endpoint[];
  meta: {
    total: number;
    count: number;
  };
}

export interface EndpointTestRequest {
  method: string;
  headers: Record<string, string>;
  body?: string;
  api_key: string;
}

export interface EndpointTestResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: unknown;
  time: number;
}

export interface SyncResult {
  synced_count: number;
  failed_count: number;
  results: Array<{
    endpoint_id: string;
    status: 'success' | 'error';
    apisix_route_id?: string;
    apisix_service_id?: string;
    error?: string;
  }>;
}

class EndpointsService {
  private dataSource = getDataSource();

  async getEndpoints(productId: string): Promise<EndpointListResponse> {
    try {
      const response = await this.dataSource.get<EndpointListResponse>(`/api/v1/products/${productId}/endpoints`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching endpoints for product ${productId}:`, error);
      throw error;
    }
  }

  async getEndpoint(productId: string, endpointId: string): Promise<Endpoint> {
    try {
      const response = await this.dataSource.get<Endpoint>(`/api/v1/products/${productId}/endpoints/${endpointId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching endpoint ${endpointId}:`, error);
      throw error;
    }
  }

  async createEndpoint(productId: string, data: EndpointCreateData): Promise<Endpoint> {
    try {
      const response = await this.dataSource.post<Endpoint>(`/api/v1/products/${productId}/endpoints`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating endpoint for product ${productId}:`, error);
      throw error;
    }
  }

  async updateEndpoint(productId: string, endpointId: string, data: EndpointUpdateData): Promise<Endpoint> {
    try {
      const response = await this.dataSource.put<Endpoint>(`/api/v1/products/${productId}/endpoints/${endpointId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating endpoint ${endpointId}:`, error);
      throw error;
    }
  }

  async deleteEndpoint(productId: string, endpointId: string): Promise<void> {
    try {
      await this.dataSource.delete(`/api/v1/products/${productId}/endpoints/${endpointId}`);
    } catch (error) {
      console.error(`Error deleting endpoint ${endpointId}:`, error);
      throw error;
    }
  }

  async syncToApisix(productId: string): Promise<SyncResult> {
    try {
      const response = await this.dataSource.post<SyncResult>(`/api/v1/products/${productId}/endpoints/sync-apisix`);
      return response.data;
    } catch (error) {
      console.error(`Error syncing endpoints to APISIX for product ${productId}:`, error);
      throw error;
    }
  }

  async syncEndpoint(productId: string, endpointId: string): Promise<SyncResult> {
    try {
      const response = await this.dataSource.post<SyncResult>(
        `/api/v1/products/${productId}/endpoints/${endpointId}/sync`
      );
      return response.data;
    } catch (error) {
      console.error(`Error syncing endpoint ${endpointId} for product ${productId}:`, error);
      throw error;
    }
  }

  async removeEndpointFromGateway(productId: string, endpointId: string): Promise<void> {
    try {
      await this.dataSource.delete(`/api/v1/products/${productId}/endpoints/${endpointId}/apisix`);
    } catch (error) {
      console.error(`Error removing endpoint ${endpointId} from gateway for product ${productId}:`, error);
      throw error;
    }
  }

  async testEndpoint(productId: string, endpointId: string, testData: EndpointTestRequest): Promise<EndpointTestResponse> {
    try {
      const response = await this.dataSource.post<EndpointTestResponse>(
        `/api/v1/products/${productId}/endpoints/${endpointId}/test`,
        testData
      );
      return response.data;
    } catch (error) {
      console.error(`Error testing endpoint ${endpointId}:`, error);
      throw error;
    }
  }
}

export const endpointsService = new EndpointsService();
